import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
  useMemo,
} from "react";
import { getKeycloak, keycloakInitOptions } from "../config/keycloak";

// 🔑  Contexte global d'authentification
const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [roles, setRoles] = useState([]);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const keycloak = useMemo(() => getKeycloak(), []);
  const initialized = useRef(false);
  const refreshInterval = useRef(null);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    (async () => {
      try {
        console.log("[KC] Initializing Keycloak…");
        const ok = await keycloak.init(keycloakInitOptions);

        if (!ok) {
          console.warn("[KC] Not authenticated after init()");
          setAuthenticated(false);
          setLoading(false);
          return;
        }

        // ✅ Authentifié
        setAuthenticated(true);
        console.log("[KC] Authenticated:", ok);

        // 🔁 Rafraîchissement automatique de token
        keycloak.onTokenExpired = () => {
          keycloak.updateToken(30).catch((err) => {
            console.error("[KC] Token refresh failed:", err);
            keycloak.logout({ redirectUri: window.location.origin });
          });
        };
        if (!refreshInterval.current) {
          refreshInterval.current = setInterval(() => {
            keycloak.updateToken(70).catch(() => {});
          }, 60000);
        }

        // 👤 Chargement du profil & rôles utilisateur
        const cid = import.meta.env.VITE_KEYCLOAK_CLIENT_ID || "portail-support";
        const profile = await keycloak.loadUserProfile().catch(() => ({}));
        const realmRoles = keycloak.realmAccess?.roles || [];
        const clientRoles = keycloak.resourceAccess?.[cid]?.roles || [];
        setRoles([...new Set([...realmRoles, ...clientRoles])]);

        setUser({
          id: keycloak.subject,
          username:
            keycloak.tokenParsed?.preferred_username || profile.username || "user",
          email: profile.email || "",
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
        });

        setLoading(false);
      } catch (err) {
        console.error("[KC] init() error:", err);
        setAuthenticated(false);
        setLoading(false);
      }
    })();

    return () => {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
    };
  }, [keycloak]);

  const logout = () => {
    try {
      if (refreshInterval.current) clearInterval(refreshInterval.current);
      keycloak.logout({ redirectUri: window.location.origin });
    } catch (e) {
      console.error("[KC] logout() error:", e);
    }
  };

  const value = useMemo(
    () => {
      const hasAnyRole = (rolesToCheck) =>
        rolesToCheck.some((r) => roles.includes(r));

      return {
        authenticated,
        user,
        roles,
        loading,
        hasAnyRole,
        logout,
        keycloak,
        canProduce: hasAnyRole(['reporting_produce', 'admin_apps', 'admin']),
        canCorrectCID: hasAnyRole(['reporting_cid', 'admin_apps', 'admin']),
        canSettings: hasAnyRole(['reporting_settings', 'admin_apps', 'admin']),
        isReadOnly: !hasAnyRole(['reporting_produce', 'reporting_cid', 'reporting_settings', 'admin_apps', 'admin']),
        isAdmin: hasAnyRole(['admin_apps', 'admin']),
      };
    },
    [authenticated, user, roles, loading, keycloak]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-blue-600 border-r-transparent mb-4"></div>
          <p className="text-gray-600 font-medium">
            Initialisation de l’authentification…
          </p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// ✅ Hook d’usage
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth doit être utilisé dans <AuthProvider>");
  return ctx;
};

export default AuthContext;
